// Data-availability warnings for composite KPI metrics. Several catalog
// metrics are sums/ratios of source measures whose histories start (or end) on
// different dates — e.g. Brand Value and Total Business Revenue blend Shopify
// with much-shorter Amazon history, Total Ad Spend includes Amazon Ads, and
// Total Branded Search Clicks blends Google Ads with Search Console. Inside the
// summed value there is no way to tell that a component was NULL/0, so periods
// outside a source's history silently understate the metric.
//
// This module derives each source's available window [first, last nonzero
// date] and evaluates per-bucket warnings. The registry's limiting sources get
// DAY-resolution bounds from one small dedicated day-granularity query (the
// only way a week/month bucket the source covers partially can be flagged —
// "missing for ANY day in the period"); every other source (calculated-metric
// leaves, registered custom series) is bounded by the already-loaded KPI rows
// (current + `compared.` YoY twins) at bucket resolution:
//   - a static registry (METRIC_AVAILABILITY_RULES) maps composite metric keys
//     to their limiting sources, mirroring the cube-measure SQL in
//     cube/model/company_data_daily.js (e.g. the DTC MER family divides by
//     total_direct_spend, which EXCLUDES Amazon, so it is not listed);
//   - user-defined calculated metrics derive their sources dynamically from
//     the expression's leaf refs, flagging both sides of the combined window.
//
// A source with no nonzero data anywhere in the loaded window produces no
// warnings at all: either the connector isn't set up (the metric never included
// that component) or data simply never landed — flagging every cell would be
// noise, not signal.

import { collectCalcLeafKeys, collectCalcRefKeys, type CalcDefsById, type CalcExpr } from './calculated-metrics';
import { COMPUTED_METRICS } from './computed-metrics';
import { deriveBucketEnd, shiftComparisonStart, type BucketGranularity } from './custom-metric-buckets';
import { getMetricCatalogEntry } from './metric-catalog';
import { toFiniteNumber } from './numeric';

// Re-exported for the existing import sites (the implementation moved next to
// the other expression walkers in calculated-metrics.ts).
export { collectCalcRefKeys };

export const AMAZON_REVENUE_SOURCE_KEY = 'metrics.amazon_total_revenue';
export const AMAZON_ADS_SPEND_SOURCE_KEY = 'metrics.amazon_ads_total_spend';
/** Search Console availability is evidenced by the Branded Searches measure. */
export const SEARCH_CONSOLE_SOURCE_KEY = 'metrics.google_search_organic_branded_search_impressions';

// Human labels for warning messages. Falls back to the metric catalog for calc
// leaves; these overrides exist because some catalog labels are group-relative
// (e.g. amazon_total_revenue's catalog label is just "Total").
const SOURCE_LABELS: Record<string, string> = {
  [AMAZON_REVENUE_SOURCE_KEY]: 'Amazon revenue',
  [AMAZON_ADS_SPEND_SOURCE_KEY]: 'Amazon Ads spend',
  [SEARCH_CONSOLE_SOURCE_KEY]: 'Search Console branded-search',
};

export interface AvailabilityRule {
  /** Source measure whose available window limits the metric. */
  source: string;
  /** Warn on buckets before the source's first nonzero date. */
  flagBefore: boolean;
  /** Warn on buckets after the source's last nonzero date. */
  flagAfter: boolean;
  /**
   * Only applies when the company's brand-value model includes Amazon
   * (`amazonEnabled`): Brand Value only blends Amazon revenue into its
   * dependent variable when the Amazon connector is connected/enabled, unlike
   * e.g. total_business_revenue_* whose SQL always sums the Amazon column.
   */
  requiresAmazonDepVar?: boolean;
}

const amazonRevenueBefore: AvailabilityRule = {
  source: AMAZON_REVENUE_SOURCE_KEY,
  flagBefore: true,
  flagAfter: false,
};
const brandValueAmazonBefore: AvailabilityRule = {
  ...amazonRevenueBefore,
  requiresAmazonDepVar: true,
};
const amazonSpendBefore: AvailabilityRule = {
  source: AMAZON_ADS_SPEND_SOURCE_KEY,
  flagBefore: true,
  flagAfter: false,
};
const searchConsoleBothSides: AvailabilityRule = {
  source: SEARCH_CONSOLE_SOURCE_KEY,
  flagBefore: true,
  flagAfter: true,
};
const searchConsoleAfter: AvailabilityRule = {
  source: SEARCH_CONSOLE_SOURCE_KEY,
  flagBefore: false,
  flagAfter: true,
};

/**
 * Composite metric key → the sources that limit its usable window. Mirrors the
 * measure SQL in cube/model/company_data_daily.js (and the brand-value dbt
 * pipeline's dependent variable for the d30/d180 metrics).
 */
export const METRIC_AVAILABILITY_RULES: Record<string, AvailabilityRule[]> = {
  // Brand Value: the ML model's dependent variable includes Amazon owned
  // revenue (when enabled), and Amazon history is typically much shorter than
  // Shopify's.
  'metrics.d30_brand_value': [brandValueAmazonBefore],
  'metrics.d180_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.ad_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.mentions_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.organic_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.search_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.subscription_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.unattributed_channel_group_brand_value': [brandValueAmazonBefore],
  'company_platform_brand_value.total_brand_value': [brandValueAmazonBefore],
  // Modeled Brand Value (campaign media cube) estimates d180 brand value, so it
  // inherits the same Amazon-revenue limit; Modeled BROAS divides it by spend.
  // Modeled Revenue / Modeled ROAS are intentionally NOT listed.
  'company_media_metrics_campaigns.estimated_d180_brand_value': [brandValueAmazonBefore],
  'company_media_metrics_campaigns.modeled_brand_value_over_spend': [brandValueAmazonBefore],

  // Total Business Revenue = Amazon total revenue + Shopify/GA revenue
  // (+ 3rd Party Retail sell-through, additive and absent-as-zero).
  'metrics.total_business_revenue_shopify': [amazonRevenueBefore],
  'metrics.total_business_revenue_ga': [amazonRevenueBefore],

  // Total Ad Spend = social + Google Ads + Amazon Ads spend, and every ratio
  // built on it. (dtc_ecomm_mer/amer and total_dtc_mer/amer divide by
  // total_direct_spend, which excludes Amazon — intentionally absent here.)
  'metrics.total_ad_spend': [amazonSpendBefore],
  'metrics.amer': [amazonSpendBefore],
  'metrics.spend_percentage_of_new_customer_revenue': [amazonSpendBefore],
  'metrics.total_business_mer': [amazonRevenueBefore, amazonSpendBefore],
  'metrics.dtc_ecomm_plus_amazon_mer': [amazonRevenueBefore, amazonSpendBefore],

  // Amazon Contribution (frontend-computed) reads Amazon revenue directly; the
  // ad-spend input has an assumption fallback, so only revenue limits it.
  'computed.amazon_contribution': [amazonRevenueBefore],
  'computed.amazon_contribution_margin': [amazonRevenueBefore],

  // dbt-estimated Amazon profitability metrics are built from Amazon sales
  // history (their applied ad spend has an assumption fallback, mirroring the
  // computed metrics above); Total Business CM includes the Amazon side.
  'metrics.amazon_gross_profit': [amazonRevenueBefore],
  'metrics.amazon_contribution_margin': [amazonRevenueBefore],
  'metrics.total_business_contribution_margin': [amazonRevenueBefore],

  // Branded search: Search Console history is both shorter-lived at the start
  // AND lags Google Ads at the end, so warn on both sides of its window.
  'metrics.total_google_search_clicks': [searchConsoleBothSides],
  'metrics.branded_search_conversion_rate': [searchConsoleBothSides],
  // The Search Console measures themselves: the connector lags a day or more
  // behind Google Ads, so the latest bucket can read 0 branded searches (a
  // -100% YoY) while the paid click measures for the same day are populated.
  // Flag only the trailing gap — before the first date there is simply no
  // history, which the zero already says.
  [SEARCH_CONSOLE_SOURCE_KEY]: [searchConsoleAfter],
  'metrics.google_search_organic_branded_search_clicks': [searchConsoleAfter],
};

/**
 * First/last date (date-only) with a nonzero value; null = none in window.
 * `resolution` records what the dates are evidenced by: 'day' bounds come from
 * the dedicated day-granularity availability query and support range checks
 * against a bucket's [start, end]; 'bucket' bounds are derived from rows at
 * the view granularity, so each date is only a bucket START — a bucket dated
 * AT `last` is evidenced as having data, and range checks would false-flag it.
 */
export interface SourceBounds {
  first: string | null;
  last: string | null;
  resolution: 'day' | 'bucket';
}

export interface AvailabilityPoint {
  /** 'YYYY-MM-DD' (longer ISO strings are truncated). */
  date: string;
  value: number | null;
}

export interface AvailabilityIndex {
  granularity: BucketGranularity;
  amazonEnabled: boolean;
  /**
   * Last date data can be expected to exist for (yesterday, local calendar):
   * range checks clamp a bucket's end to it so an in-progress week/month is
   * not flagged for days that simply have not happened yet.
   */
  lastExpectedDate: string;
  /**
   * First date the day-granularity availability query fetched, or null when
   * no daily evidence was provided. Range checks clamp a bucket's start to it:
   * day bounds are truncated at the fetched window's edge, and the oldest
   * weekly comparison bucket starts before that edge on 6 of 7 weekdays — a
   * source with history predating the window must not read the window edge as
   * its true start (a false "missing part" warning for mature companies).
   */
  firstFetchedDate: string | null;
  /**
   * Comparison-bucket start resolver for EXPLICIT compare windows (see
   * resolveComparisonDate); undefined on the default YoY dashboard path.
   * Carried on the index so row builders annotating warnings use the same
   * shift the index's own bounds were computed with.
   */
  comparisonStartOf?: (bucketStart: string) => string;
  /** Available window of a source measure, memoized per key. */
  getBounds(sourceKey: string): SourceBounds;
  /** Human label for a source key (overrides → registered → catalog → key). */
  getLabel(sourceKey: string): string;
  /**
   * Registers a non-row-backed source (e.g. a `custom.<id>` series referenced
   * from a calculated metric) so calc warnings can bound it too.
   */
  registerSource(key: string, label: string, points: AvailabilityPoint[]): void;
}

function toDateOnly(value: unknown): string | null {
  if (typeof value !== 'string' || value.length < 10) return null;
  return value.slice(0, 10);
}

function boundsOfPoints(points: AvailabilityPoint[], resolution: 'day' | 'bucket' = 'bucket'): SourceBounds {
  let first: string | null = null;
  let last: string | null = null;
  for (const point of points) {
    const date = toDateOnly(point.date);
    if (!date || !((point.value ?? 0) > 0)) continue;
    if (first === null || date < first) first = date;
    if (last === null || date > last) last = date;
  }
  return { first, last, resolution };
}

/** Local calendar date (no UTC conversion — matches the window builders). */
function localDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The comparison bucket's own date for one merged row: the real date carried by
 * the YoY merge under `compared.<dateKey>` when the comparison bucket existed,
 * else the same prior-period shift the custom-metric series uses.
 *
 * `comparisonStartOf` overrides the fallback shift for EXPLICIT compare
 * windows (comparisonShiftForWindows) so the derived date matches the window
 * the data was actually compared against; absent → the default year-back rule.
 */
export function resolveComparisonDate(
  row: Record<string, unknown>,
  dateKey: string,
  bucketDate: string,
  granularity: BucketGranularity,
  comparisonStartOf?: (bucketStart: string) => string,
): string {
  return (
    toDateOnly(row[`compared.${dateKey}`]) ??
    (comparisonStartOf ? comparisonStartOf(bucketDate.slice(0, 10)) : shiftComparisonStart(bucketDate.slice(0, 10), granularity))
  );
}

/** One dataset the availability index can evidence bounds from. */
export interface AvailabilityRowSet {
  rows: ReadonlyArray<Record<string, unknown>> | null;
  dateKey: string;
}

/**
 * Builds the availability index over the merged KPI rows (the main `metrics.*`
 * dataset). Bounds are computed lazily per source key from both the current
 * values and their `compared.` twins (dated by the comparison bucket's own
 * date), so the visible window plus its full comparison window evidence each
 * source's history.
 *
 * `extraRowSets` supplies the sibling cube datasets (brand value, media
 * campaigns, pivoted initiatives) so calculated-metric operands from those
 * sources get real bounds too. A source key's column exists in exactly one
 * dataset; the others contribute only missing→0 points, which never move the
 * nonzero bounds, so scanning every set is safe.
 *
 * When `dailyRows` is provided (the dedicated day-granularity availability
 * query), sources present in it get DAY-resolution bounds instead — the only
 * evidence that can flag a week/month bucket the source covers partially. The
 * daily query fetches just the registry's limiting source measures; every
 * other source (calculated-metric leaves, sibling-dataset leaves, registered
 * custom series) keeps bucket-resolution bounds and start-date-only checks.
 */
export function buildAvailabilityIndex(args: {
  rows: ReadonlyArray<Record<string, unknown>> | null;
  dateKey: string;
  granularity: BucketGranularity;
  amazonEnabled: boolean;
  extraRowSets?: ReadonlyArray<AvailabilityRowSet>;
  dailyRows?: ReadonlyArray<Record<string, unknown>> | null;
  dailyDateKey?: string;
  /** First day the daily availability query fetched (its dateRange start). */
  dailyWindowStart?: string;
  /** Explicit-compare-window shift override (see AvailabilityIndex). */
  comparisonStartOf?: (bucketStart: string) => string;
}): AvailabilityIndex {
  const { rows, dateKey, granularity, amazonEnabled, extraRowSets, dailyRows, dailyDateKey, dailyWindowStart, comparisonStartOf } = args;
  const boundsByKey = new Map<string, SourceBounds>();
  const registeredLabels = new Map<string, string>();
  const rowSets: AvailabilityRowSet[] = [{ rows, dateKey }, ...(extraRowSets ?? [])];

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const computeDailyBounds = (sourceKey: string): SourceBounds | null => {
    if (!dailyRows || dailyRows.length === 0 || !dailyDateKey) return null;
    if (!(sourceKey in dailyRows[0])) return null;
    const points: AvailabilityPoint[] = [];
    for (const row of dailyRows) {
      const date = toDateOnly(row[dailyDateKey]);
      if (!date) continue;
      points.push({ date, value: toFiniteNumber(row[sourceKey]) });
    }
    return boundsOfPoints(points, 'day');
  };

  const computeRowBounds = (sourceKey: string): SourceBounds => {
    const points: AvailabilityPoint[] = [];
    for (const rowSet of rowSets) {
      if (!rowSet.rows || rowSet.rows.length === 0) continue;
      for (const row of rowSet.rows) {
        const date = toDateOnly(row[rowSet.dateKey]);
        if (!date) continue;
        points.push({ date, value: toFiniteNumber(row[sourceKey]) });
        const comparedKey = `compared.${sourceKey}`;
        if (comparedKey in row) {
          points.push({
            date: resolveComparisonDate(row, rowSet.dateKey, date, granularity, comparisonStartOf),
            value: toFiniteNumber(row[comparedKey]),
          });
        }
      }
    }
    return boundsOfPoints(points);
  };

  return {
    granularity,
    amazonEnabled,
    lastExpectedDate: localDateOnly(yesterday),
    firstFetchedDate: dailyWindowStart ? dailyWindowStart.slice(0, 10) : null,
    comparisonStartOf,
    getBounds(sourceKey: string): SourceBounds {
      const cached = boundsByKey.get(sourceKey);
      if (cached) return cached;
      const bounds = computeDailyBounds(sourceKey) ?? computeRowBounds(sourceKey);
      boundsByKey.set(sourceKey, bounds);
      return bounds;
    },
    getLabel(sourceKey: string): string {
      return (
        SOURCE_LABELS[sourceKey] ??
        registeredLabels.get(sourceKey) ??
        getMetricCatalogEntry(sourceKey)?.label ??
        COMPUTED_METRICS[sourceKey]?.label ??
        sourceKey
      );
    },
    registerSource(key: string, label: string, points: AvailabilityPoint[]): void {
      registeredLabels.set(key, label);
      boundsByKey.set(key, boundsOfPoints(points));
    },
  };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' → 'Mar 12, 2025' (no timezone math — the string is date-only). */
export function formatAvailabilityDate(dateOnly: string): string {
  const [year, month, day] = dateOnly.slice(0, 10).split('-');
  const monthName = MONTH_NAMES[Number(month) - 1] ?? month;
  return `${monthName} ${Number(day)}, ${year}`;
}

function ruleWarning(
  index: AvailabilityIndex,
  rule: AvailabilityRule,
  bucketDate: string,
): { side: 'before' | 'after'; message: string } | null {
  if (rule.requiresAmazonDepVar && !index.amazonEnabled) return null;
  const bounds = index.getBounds(rule.source);
  // No nonzero data anywhere in the loaded window → the source isn't in play.
  if (bounds.first === null || bounds.last === null) return null;

  const start = bucketDate.slice(0, 10);
  const label = index.getLabel(rule.source);

  // Day-resolution bounds support the "missing for ANY day in the period"
  // check: compare the bucket's full [start, end] range, with the end clamped
  // to yesterday so an in-progress week/month is not flagged for days that
  // have not happened yet. Bucket-resolution bounds only carry bucket START
  // dates, so they keep the start-only comparison (a bucket dated AT `last`
  // is itself the evidence that data exists there).
  const rangeCheck = bounds.resolution === 'day';
  const bucketEnd = rangeCheck ? deriveBucketEnd(start, index.granularity) : start;
  const end = rangeCheck && bucketEnd > index.lastExpectedDate ? index.lastExpectedDate : bucketEnd;
  // The whole bucket is in the future — nothing can be missing yet.
  if (rangeCheck && end < start) return null;

  // Day bounds are truncated at the fetched window's start, so a bucket
  // reaching before that edge (the oldest weekly comparison bucket starts on
  // the week-start day before it) must not read the edge as the source's true
  // start — clamp the bucket start to the fetched window, mirroring the
  // lastExpectedDate clamp on the other side.
  const effectiveStart = rangeCheck && index.firstFetchedDate !== null && start < index.firstFetchedDate ? index.firstFetchedDate : start;

  if (rule.flagBefore && effectiveStart < bounds.first) {
    const partial = rangeCheck && end >= bounds.first;
    return {
      side: 'before',
      message: partial
        ? `${label} data starts ${formatAvailabilityDate(bounds.first)}; this period begins earlier, so the value is missing part of its ${label} component.`
        : `${label} data starts ${formatAvailabilityDate(bounds.first)}; this period is earlier, so the value is missing its ${label} component.`,
    };
  }
  if (rule.flagAfter && end > bounds.last) {
    const partial = start <= bounds.last;
    return {
      side: 'after',
      message: partial
        ? `${label} data ends ${formatAvailabilityDate(bounds.last)}; this period extends later, so the value is missing part of its ${label} component.`
        : `${label} data ends ${formatAvailabilityDate(bounds.last)}; this period is later, so the value is missing its ${label} component.`,
    };
  }
  return null;
}

function collectWarnings(index: AvailabilityIndex, rules: AvailabilityRule[], bucketDate: string): string | null {
  const messages: string[] = [];
  const seenSides = new Set<string>();
  for (const rule of rules) {
    const warning = ruleWarning(index, rule, bucketDate);
    if (!warning) continue;
    // Dedupe by source+direction so overlapping rules don't repeat themselves.
    const sideKey = `${rule.source}:${warning.side}`;
    if (seenSides.has(sideKey)) continue;
    seenSides.add(sideKey);
    messages.push(warning.message);
  }
  return messages.length > 0 ? messages.join(' ') : null;
}

/**
 * Warning text for one composite metric at one bucket date (either the current
 * bucket's date or the comparison bucket's date), or null when the bucket is
 * inside every source's available window. Metrics without registry rules never
 * warn.
 */
export function availabilityWarningsForMetric(index: AvailabilityIndex, metricKey: string, bucketDate: string): string | null {
  const rules = METRIC_AVAILABILITY_RULES[metricKey];
  if (!rules || rules.length === 0) return null;
  return collectWarnings(index, rules, bucketDate);
}

/**
 * Warning text for a user-defined calculated metric at one bucket date. Every
 * expression leaf is treated as a source bounded on BOTH sides of its window
 * (a calculated metric combines multiple metrics, so any leaf outside its
 * history silently zeroes part of the formula), and leaves that are themselves
 * composite (e.g. total_ad_spend) also contribute their own registry rules.
 * `computed.*` leaves are represented by their registry rules alone — their
 * value is not a row column, but their limiting inputs are. Nested `calc.<id>`
 * leaves expand to the referenced def's own leaves via `defs` (a dangling id
 * contributes nothing).
 */
export function availabilityWarningsForCalc(
  index: AvailabilityIndex,
  expr: CalcExpr,
  bucketDate: string,
  defs?: CalcDefsById,
): string | null {
  const rules: AvailabilityRule[] = [];
  const seenSources = new Set<string>();
  const pushRule = (rule: AvailabilityRule): void => {
    const key = `${rule.source}:${rule.flagBefore}:${rule.flagAfter}:${rule.requiresAmazonDepVar ?? false}`;
    if (seenSources.has(key)) return;
    seenSources.add(key);
    rules.push(rule);
  };

  for (const refKey of collectCalcLeafKeys(expr, defs)) {
    for (const rule of METRIC_AVAILABILITY_RULES[refKey] ?? []) {
      pushRule(rule);
    }
    // The leaf's own series bounds the formula on both sides. Computed leaves
    // have no row column to bound — their registry rules above cover them.
    if (!refKey.startsWith('computed.')) {
      pushRule({ source: refKey, flagBefore: true, flagAfter: true });
    }
  }

  return collectWarnings(index, rules, bucketDate);
}
